
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface QuickMenuProps {
  isMobile?: boolean;
}

const QuickMenu = ({ isMobile = false }: QuickMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'resources', label: 'Resources' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'class-schedule', label: 'Schedule' },
    { id: 'floorplan', label: 'Floorplan Financing' },
    { id: 'giada', label: 'GIADA Pre-License' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Contact' },
    { id: 'enroll', label: 'Enroll' }
  ];

  // Smooth scroll helper with header offset
  const scrollToId = (id: string) => {
    // Special handling for schedule - navigate to consultation page
    if (id === 'class-schedule') {
      navigate('/consulting');
      setIsOpen(false);
      return;
    }

    const h = document.querySelector('.site-header')?.getBoundingClientRect().height || 80;
    const el = document.getElementById(id);
    if (!el) return;
    
    const y = el.getBoundingClientRect().top + window.pageYOffset - h;
    window.scrollTo({ top: y, behavior: 'smooth' });
    
    // Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'menu_item_click', {
        section_id: id,
        menu_location: isMobile ? 'sticky' : 'header'
      });
    }
    
    setIsOpen(false);
  };

  // Scrollspy with IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0.1
      }
    );

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  // Handle ESC key and focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
      
      if (e.key === 'Tab' && isOpen && menuRef.current) {
        const focusableElements = menuRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
        
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMenuToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    
    // Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'menu_open', {
        location: isMobile ? 'sticky' : 'header'
      });
    }
  };

  const baseClasses = isMobile 
    ? "fixed bottom-6 right-6 z-50" 
    : "relative";

  return (
    <div className={baseClasses}>
      {/* Menu Button */}
      <button
        ref={buttonRef}
        onClick={handleMenuToggle}
        className={`
          ${isMobile 
            ? 'w-14 h-14 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-full shadow-lg hover:shadow-xl' 
            : 'px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20'
          } 
          flex items-center justify-center font-medium transition-all duration-200 cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2
        `}
        aria-expanded={isOpen}
        aria-controls="quick-menu-panel"
        aria-label="Quick navigation menu"
      >
        {isMobile ? (
          <i className={`ri-${isOpen ? 'close' : 'menu'}-line text-xl transition-transform duration-200`}></i>
        ) : (
          <>
            <i className="ri-menu-line mr-2"></i>
            Menu
          </>
        )}
      </button>

      {/* Menu Panel */}
      {isOpen && (
        <div
          ref={menuRef}
          id="quick-menu-panel"
          className={`
            ${isMobile 
              ? 'absolute bottom-16 right-0 w-64' 
              : 'absolute top-full right-0 mt-2 w-56'
            }
            bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50
            animate-in fade-in slide-in-from-top-2 duration-200
          `}
          role="menu"
          aria-labelledby="quick-menu-button"
        >
          <div className="px-4 py-2 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Quick Navigation</h3>
          </div>
          
          <div className="py-2 max-h-80 overflow-y-auto">
            {sections.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollToId(id)}
                className={`
                  w-full px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors duration-150
                  flex items-center justify-between cursor-pointer
                  focus:outline-none focus:bg-gray-50 focus:ring-2 focus:ring-inset focus:ring-yellow-400
                  ${activeSection === id ? 'bg-yellow-50 text-yellow-700 font-medium' : 'text-gray-700'}
                `}
                role="menuitem"
              >
                <span>{label}</span>
                {activeSection === id && (
                  <i className="ri-arrow-right-s-line text-yellow-600"></i>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickMenu;
